import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../css/checkout.css";

const cardTestMapping = {
  "Green CSCS Card": ["Labourers card / Operatives"],
};

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const selectedDate = location.state?.dateString || "No date selected";
  const remainingSlots = location.state?.remainingSlots || 1;

  const maxAttendeesAllowed = Math.min(remainingSlots, 5);
  const PRICE_PER_PERSON = 100;

  const baseAttendee = {
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    number: "",
    nationalInsurance: "",
    languageSupport: "",
    reasonableAdjustments: "",
  };

  const [attendees, setAttendees] = useState([{ ...baseAttendee }]);

  const [courseDetails, setCourseDetails] = useState({
    time: "09:00 AM - 17:00 PM (Full Day)",
    location: "West Brom",
    cscsCard: "Green CSCS Card",
    citbTest: cardTestMapping["Green CSCS Card"][0],
  });

  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleAttendeeChange = (index, e) => {
    const { name, value } = e.target;
    const updatedAttendees = [...attendees];

    if (name === "nationalInsurance") {
      // 1. Remove all spaces and non-alphanumeric chars, then make uppercase
      const rawValue = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

      // 2. Format as XX 11 22 33 X
      let formattedValue = rawValue.slice(0, 2);
      if (rawValue.length > 2) formattedValue += " " + rawValue.slice(2, 4);
      if (rawValue.length > 4) formattedValue += " " + rawValue.slice(4, 6);
      if (rawValue.length > 6) formattedValue += " " + rawValue.slice(6, 8);
      if (rawValue.length > 8) formattedValue += " " + rawValue.slice(8, 9);

      updatedAttendees[index][name] = formattedValue;
    } else if (name === "number") {
      // 1. Strip all non-numeric characters
      let rawValue = value.replace(/\D/g, "");

      // 2. Force it to start with 0, then 07
      if (rawValue.length === 1 && rawValue !== "0") {
        rawValue = ""; // If they type anything other than '0' first, clear it
      } else if (rawValue.length >= 2 && !rawValue.startsWith("07")) {
        rawValue = "0"; // If the second digit isn't '7', revert back to '0'
      }

      // 3. Limit to standard UK mobile length (11 digits)
      updatedAttendees[index][name] = rawValue.slice(0, 11);
    } else {
      updatedAttendees[index][name] = value;
    }

    setAttendees(updatedAttendees);
  };

  const handleCourseChange = (e) => {
    const { name, value } = e.target;
    setCourseDetails({ ...courseDetails, [name]: value });
  };

  const addAttendee = () => {
    if (attendees.length < maxAttendeesAllowed) {
      setAttendees([...attendees, { ...baseAttendee }]);
    }
  };

  const removeAttendee = (index) => {
    const updated = attendees.filter((_, i) => i !== index);
    setAttendees(updated);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setShowModal(true);
  };

  const proceedToPayment = async () => {
    setIsLoading(true);

    const formattedAttendees = attendees.map((attendee) => ({
      ...attendee,
      fullName: `${attendee.firstName.trim()} ${attendee.middleName.trim()} ${attendee.lastName.trim()}`,
    }));

    const totalPrice = attendees.length * PRICE_PER_PERSON;

    try {
      const response = await fetch(
        "https://getyourcscs.co.uk/api/create-checkout-session",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            attendees: formattedAttendees,
            totalPrice: totalPrice,
            date: selectedDate,
            ...courseDetails,
          }),
        },
      );

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert("Payment initialization failed. Please try again.");
      }
    } catch (error) {
      console.error("Error redirecting to Stripe:", error);
    } finally {
      setIsLoading(false);
      setShowModal(false);
    }
  };

  const isMultiple = attendees.length > 1;

  return (
    <div className="page-wrapper flat-dark-theme">
      <Helmet>
        <title>Get Your CSCS | Checkout</title>
        <meta
          name="description"
          content="Complete your booking for your CSCS card training and CITB test. Secure your slot at our Walsall or West Bromwich test centres."
        />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://www.getyourcscs.co.uk/checkout" />
      </Helmet>

      <div className="checkout-container">
        <div className="glass-form-card">
          <h2>Complete Your Booking</h2>

          <form onSubmit={handleFormSubmit} className="checkout-form">
            {/* 1. DYNAMIC ATTENDEE FIELDS (Using Fragment to not break CSS Grid) */}
            {attendees.map((attendee, index) => (
              <React.Fragment key={index}>
                {/* Full-width header spanning the grid */}
                <div
                  style={{
                    gridColumn: "1 / -1",
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "10px",
                    marginTop: index > 0 ? "20px" : "0",
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "1.2rem",
                      color: "var(--accent-color, #4facfe)",
                    }}
                  >
                    {isMultiple ? `Attendee ${index + 1}` : "Your Details"}
                  </h3>
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => removeAttendee(index)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#ff4d4f",
                        cursor: "pointer",
                        fontSize: "0.9rem",
                        padding: "5px",
                      }}
                    >
                      ✕ Remove
                    </button>
                  )}
                </div>

                {/* NAME FIELDS */}
                <div className="form-group">
                  <label>
                    First Name <span>*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    required
                    onChange={(e) => handleAttendeeChange(index, e)}
                    value={attendee.firstName}
                    placeholder="John"
                  />
                </div>

                <div className="form-group">
                  <label>
                    Middle Name <span>*</span>
                  </label>
                  <input
                    type="text"
                    name="middleName"
                    required
                    onChange={(e) => handleAttendeeChange(index, e)}
                    value={attendee.middleName}
                    placeholder="William"
                  />
                </div>

                <div className="form-group">
                  <label>
                    Last Name <span>*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    required
                    onChange={(e) => handleAttendeeChange(index, e)}
                    value={attendee.lastName}
                    placeholder="Doe"
                  />
                </div>

                {/* CONTACT FIELDS */}
                <div className="form-group">
                  <label>
                    Email Address <span>*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    onChange={(e) => handleAttendeeChange(index, e)}
                    value={attendee.email}
                    placeholder="john@example.com"
                  />
                </div>

                <div className="form-group">
                  <label>
                    Phone Number <span>*</span>
                  </label>
                  <input
                    type="tel"
                    name="number"
                    required
                    maxLength="11" // Hard lock characters in HTML
                    onChange={(e) => handleAttendeeChange(index, e)}
                    value={attendee.number}
                    placeholder="07123456789"
                  />
                </div>

                {/* NATIONAL INSURANCE */}
                <div className="form-group">
                  <label>National Insurance Number</label>
                  <input
                    type="text"
                    name="nationalInsurance"
                    maxLength="13" // Prevents typing beyond "XX 11 22 33 X" length
                    onChange={(e) => handleAttendeeChange(index, e)}
                    value={attendee.nationalInsurance}
                    placeholder="QQ 12 34 56 A"
                    style={{ textTransform: "uppercase" }}
                  />
                </div>

                {/* SUPPORT */}
                <div className="form-group">
                  <label>Language Support (Optional)</label>
                  <input
                    type="text"
                    name="languageSupport"
                    onChange={(e) => handleAttendeeChange(index, e)}
                    value={attendee.languageSupport}
                    placeholder="E.g., Voiceover in Polish, interpreter needed"
                  />
                </div>

                <div className="form-group">
                  <label>Reasonable Adjustments (Optional)</label>
                  <textarea
                    name="reasonableAdjustments"
                    onChange={(e) => handleAttendeeChange(index, e)}
                    value={attendee.reasonableAdjustments}
                    placeholder="Please list any physical access or learning adjustments required..."
                    rows="3"
                  ></textarea>
                </div>

                {/* Divider between attendees */}
                {isMultiple && index !== attendees.length - 1 && (
                  <div
                    style={{
                      gridColumn: "1 / -1",
                      height: "1px",
                      background: "rgba(255,255,255,0.1)",
                      width: "100%",
                      margin: "20px 0",
                    }}
                  ></div>
                )}
              </React.Fragment>
            ))}

            {/* 2. ADD ATTENDEE BUTTON (Forced to span full width below customer fields) */}
            {attendees.length < maxAttendeesAllowed && (
              <div
                style={{
                  gridColumn: "1 / -1",
                  width: "100%",
                  marginTop: "15px",
                  marginBottom: "35px",
                }}
              >
                <button
                  type="button"
                  onClick={addAttendee}
                  style={{
                    display: "block",
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "14px",
                    background: "rgba(255, 255, 255, 0.03)",
                    color: "var(--text-primary, #fff)",
                    border: "2px dashed rgba(255, 255, 255, 0.3)",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "1rem",
                    fontWeight: "600",
                    transition: "all 0.2s ease",
                  }}
                  onMouseOver={(e) =>
                    (e.target.style.background = "rgba(255, 255, 255, 0.08)")
                  }
                  onMouseOut={(e) =>
                    (e.target.style.background = "rgba(255, 255, 255, 0.03)")
                  }
                >
                  + Add another person (Max {maxAttendeesAllowed} slots
                  remaining)
                </button>
              </div>
            )}

            {/* 3. COURSE DETAILS TITLE (Forced to span full width) */}
            <div
              style={{
                gridColumn: "1 / -1",
                width: "100%",
                borderTop: "1px solid rgba(255,255,255,0.1)",
                paddingTop: "25px",
                marginBottom: "10px",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "1.2rem",
                  color: "var(--accent-color, #4facfe)",
                }}
              >
                {isMultiple ? "Group Course Details" : "Course Details"}
              </h3>
            </div>

            {/* COURSE DETAILS FIELDS */}
            <div className="form-group">
              <label>
                Test Centre Location <span>*</span>
              </label>
              <select
                name="location"
                onChange={handleCourseChange}
                value={courseDetails.location}
              >
                <option value="West Brom">West Brom</option>
              </select>
            </div>

            <div className="form-group disabled-group">
              <label>
                CSCS Card Type <span>*</span>
              </label>
              <input
                type="text"
                value="Green CSCS Card"
                disabled
                title="This package is currently locked to Green Cards only"
              />
            </div>

            <div className="form-group disabled-group">
              <label>
                Required CITB Test <span>*</span>
              </label>
              <input type="text" value={courseDetails.citbTest} disabled />
            </div>

            <div className="form-group disabled-group">
              <label>
                Training Time <span>*</span>
              </label>
              <input type="text" value={courseDetails.time} disabled />
            </div>

            <div className="form-group disabled-group">
              <label>
                Selected Date <span>*</span>
              </label>
              <input type="text" value={selectedDate} disabled />
            </div>

            <div
              className="id-reminder-note"
              style={{ gridColumn: "1 / -1", marginTop: "20px" }}
            >
              <strong>⚠️ Important:</strong>{" "}
              {isMultiple ? "All attendees must bring" : "You must bring"} a
              valid photographic ID (Passport or UK Driving Licence) on the day
              of the test. Failure to do so will result in tests being cancelled
              without a refund.
            </div>

            <button
              type="submit"
              className="btn-primary form-submit-btn"
              style={{ gridColumn: "1 / -1" }}
              disabled={selectedDate === "No date selected"}
            >
              {selectedDate === "No date selected"
                ? "Please Select a Date First"
                : `Proceed to Pay Deposit (£${attendees.length * PRICE_PER_PERSON})`}
            </button>
          </form>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="glass-modal">
            <h3>Are you sure?</h3>
            <p>
              You are about to book the{" "}
              <strong>{courseDetails.citbTest}</strong> for a{" "}
              <strong>{courseDetails.cscsCard}</strong> in{" "}
              <strong>{courseDetails.location}</strong> on{" "}
              <strong>{selectedDate}</strong>
              {isMultiple && (
                <>
                  {" "}
                  for <strong>{attendees.length} people</strong>
                </>
              )}
              .
            </p>
            <p>
              Total Cost:{" "}
              <strong>£{attendees.length * PRICE_PER_PERSON}</strong>
            </p>
            <p>You will now be redirected to our secure payment gateway.</p>

            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setShowModal(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                className="btn-confirm"
                onClick={proceedToPayment}
                disabled={isLoading}
              >
                {isLoading ? "Connecting..." : "Yes, Proceed"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
